import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Briefcase, Users, CheckCircle, Send } from 'lucide-react'

const Careers = () => {
  React.useEffect(() => {
    document.title = "Careers & Job Opportunities - Work with Shrijaa Outsourcing Consultants";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Explore job openings and career opportunities with Shrijaa Outsourcing Consultants. Apply online for staffing, HR, housekeeping, and executive roles in Hyderabad.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/careers');
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skill: '',
    education: ''
  })
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const { name, email, phone, skill, education } = formData
    const message = `Career Application:%0AName: ${encodeURIComponent(name)}%0AEmail: ${encodeURIComponent(email || '—')}%0APhone: ${encodeURIComponent(phone)}%0ASkill: ${encodeURIComponent(skill || '—')}%0AEducation: ${encodeURIComponent(education)}`
    window.open(`https://wa.me/919360012365?text=${message}`, '_blank')
    setSubmitted(true)
    setTimeout(() => setSubmitted(false), 3000)
    setFormData({ name: '', email: '', phone: '', skill: '', education: '' })
  }

  const benefits = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Collaborative Environment',
      description: 'Work with a team of experienced professionals who support and mentor each other.'
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: 'Career Development',
      description: 'Access to training programs, conferences, and opportunities for professional growth.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Competitive Benefits',
      description: 'Comprehensive health insurance, retirement plans, and performance-based bonuses.'
    }
  ]

  const companyCulture = [
    'Work-life balance and flexible schedules',
    'Remote work opportunities',
    'Professional development budget',
    'Team building events and activities',
    'Recognition and reward programs',
    'Innovation and creativity encouraged'
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Join Our Team</h1>
            <p className="text-lg md:text-xl text-primary-100">
              Build your career with a company that values growth, innovation, and making a difference
            </p>
            <div className="w-20 h-1 bg-accent-500 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Why Work With Us?
            </h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">
              Join a team that's passionate about connecting people with opportunities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-primary-600 mb-4 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Company Culture */}
          <div className="bg-secondary-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-primary-800 mb-6 text-center">
              Our Company Culture
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {companyCulture.map((item, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-secondary-600">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Application Form */}
      <section className="section-padding bg-secondary-50">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Apply Now
            </h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">
              Interested in joining our team? Fill out the form below and we'll get back to you.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="bg-white rounded-lg shadow-lg p-8 space-y-6"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-secondary-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-secondary-700 mb-2">
                  Email Address <span className="text-secondary-500 font-normal">(optional)</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={255}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    maxLength={15}
                    placeholder="Enter your phone number"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="skill" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Skill <span className="text-secondary-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="skill"
                    name="skill"
                    value={formData.skill}
                    onChange={handleChange}
                    maxLength={200}
                    placeholder="e.g. Driving, housekeeping, MS Office"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="education" className="block text-sm font-semibold text-secondary-700 mb-2">
                  Education / Qualification <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="education"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  placeholder="e.g. B.Com, MBA, 12th Pass, ITI"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                />
              </div>

              <button
                type="submit"
                className="btn-primary w-full flex items-center justify-center text-lg py-3"
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Application
              </button>

              {submitted && (
                <div className="text-center text-green-600 font-semibold">
                  ✓ Application submitted successfully!
                </div>
              )}
            </motion.form>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Application Process
            </h2>
            <p className="text-xl text-secondary-600">
              Here's what to expect when you apply
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Apply Online',
                description: 'Submit your details through our online application form.'
              },
              {
                step: '02',
                title: 'Initial Screening',
                description: 'Our team will review your application and may conduct a brief phone screening.'
              },
              {
                step: '03',
                title: 'Interview Process',
                description: 'Meet with our team through video or in-person interviews to discuss the role.'
              },
              {
                step: '04',
                title: 'Decision & Onboarding',
                description: 'We\'ll make our decision and guide you through the onboarding process.'
              }
            ].map((process, index) => (
              <motion.div
                key={process.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">
                  {process.title}
                </h3>
                <p className="text-secondary-600">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Careers
